import React, { useCallback, useMemo, useState, type PropsWithChildren, Fragment } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql, useMutation } from 'react-relay';
import type { OnSubmitCallbacks } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/types.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { useFieldInlineEditActions } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/index.tsx';
import type { ValidationFieldProps } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/field-inline-edit-lite/types.tsx';
import { FieldInlineEditLiteWithEntryPoint } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/index.tsx';
import { useInlineEditFieldInjections } from '@atlassian/jira-issue-field-injections/src/controllers/inline-edit-injections-context/index.tsx';
import MultiGroupPickerEditViewEntryPoint from '@atlassian/jira-issue-field-multi-group-picker-editview-full/src/entrypoint.tsx';
import type {
	GroupOption,
	MultiGroupPickerEditViewProps,
	NullableMultiGroupPickerOption,
} from '@atlassian/jira-issue-field-multi-group-picker-editview-full/src/ui/multi-group-picker/types.tsx';
import { MultiGroupPickerReadView } from '@atlassian/jira-issue-field-multi-group-picker-readview-full/src/ui/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { createMutationErrorHandlers } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/utils/extract-error-message.tsx';
import type { ui_issueFieldMultiGroupPickerInlineEditFull_MultiGroupPickerInlineEditViewWithIsEditable_fragmentRef$key } from './__generated__/ui_issueFieldMultiGroupPickerInlineEditFull_MultiGroupPickerInlineEditViewWithIsEditable_fragmentRef.graphql';
import type { ui_issueFieldMultiGroupPickerInlineEditFull_MultiGroupPickerInlineEditView_fragmentRef$key } from './__generated__/ui_issueFieldMultiGroupPickerInlineEditFull_MultiGroupPickerInlineEditView_fragmentRef.graphql';
import type { ui_multiGroupPicker_issueFieldMultiGroupPicker_MultiGroupPickerField_Mutation as MultiGroupPickerMutation } from './__generated__/ui_multiGroupPicker_issueFieldMultiGroupPicker_MultiGroupPickerField_Mutation.graphql';
import type {
	MultiGroupPickerInlineEditViewProps,
	MultiGroupPickerInlineEditViewWithIsEditableProps,
} from './types.tsx';

/** Determine if the updated edit view value is equal to our original value before submitting a mutation. */
const isEqualOption = (a: NullableMultiGroupPickerOption, b: NullableMultiGroupPickerOption) => {
	if (a === null || b === null) return a === b;
	if (a.length !== b.length) return false;

	const aIdSet = new Set(a.map((option) => option.id));
	return b.every((option) => aIdSet.has(option.id));
};

const MaybeUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="issue-field-multi-group-picker-read-view" mode="list">
			{children}
		</UFOSegment>
	),
	Fragment,
);

/**
 * Inline edit will handle the switching behaviour between the 'readView' and 'editView' components. This variant allows
 * consumers to define their own value to determine whether the field is editable.
 *
 * In most cases consumers should use `MultiGroupPickerInlineEditView` which will enforce that the user has permission to
 * edit the field within the issue view. However, this component can be used for experiences that have differing
 * permissions or want finer control for how this data is retrieved, e.g. lazy loading editability.
 *
 * @param props [MultiGroupPickerInlineEditViewWithIsEditableProps](./types.tsx)
 */
export const MultiGroupPickerInlineEditViewWithIsEditable = ({
	attributes,
	editViewPopup,
	editViewPopupAlignBlock,
	editViewPopupTarget,
	editViewPopupWidth,
	fragmentRef,
	isEditable,
	isEditing: startWithEditViewOpen = false,
	isTruncated,
	menuPosition,
	onSubmit,
	onSubmitFailed,
	onSubmitSucceeded,
	readViewFitContainerHeight,
	isTruncatedWithHoverPopover,
}: MultiGroupPickerInlineEditViewWithIsEditableProps): React.JSX.Element => {
	// #region Relay
	const data =
		useFragment<ui_issueFieldMultiGroupPickerInlineEditFull_MultiGroupPickerInlineEditViewWithIsEditable_fragmentRef$key>(
			// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
			graphql`
				fragment ui_issueFieldMultiGroupPickerInlineEditFull_MultiGroupPickerInlineEditViewWithIsEditable_fragmentRef on JiraMultipleGroupPickerField {
					id
					name
					fieldId
					type
					...ui_issueFieldMultiGroupPickerReadviewFull_MultiGroupPickerReadView
					selectedGroupsConnection {
						edges {
							node {
								id
								groupId
								name
							}
						}
					}
				}
			`,
			fragmentRef,
		);

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [commit] = useMutation<MultiGroupPickerMutation>(graphql`
		mutation ui_multiGroupPicker_issueFieldMultiGroupPicker_MultiGroupPickerField_Mutation(
			$input: JiraUpdateMultipleGroupPickerFieldInput!
		) @raw_response_type {
			jira @optIn(to: ["JiraIssueFieldMutations"]) {
				updateMultipleGroupPickerField(input: $input) {
					success
					errors {
						message
					}
					field {
						...ui_issueFieldMultiGroupPickerInlineEditFull_MultiGroupPickerInlineEditViewWithIsEditable_fragmentRef
					}
				}
			}
		}
	`);
	// #endregion Relay

	// #region Common state
	const { id: uniqueFieldId, name, selectedGroupsConnection, fieldId, type: fieldType } = data;

	const initialValue: NullableMultiGroupPickerOption = useMemo(() => {
		if (!selectedGroupsConnection?.edges) {
			return null;
		}

		return selectedGroupsConnection.edges.map(
			(edge): GroupOption => ({
				id: edge?.node?.id ?? '',
				groupId: edge?.node?.groupId ?? '',
				name: edge?.node?.name ?? '',
				type: 'group',
			}),
		);
	}, [selectedGroupsConnection?.edges]);

	const [updatedValue, setUpdatedValue] = useState<NullableMultiGroupPickerOption>(initialValue);

	const { overriding } = useInlineEditFieldInjections();
	const { overrideLabel, overrideIsEditable, overrideFieldOptionsFilter } = overriding;

	const fieldName = useMemo(() => overrideLabel(name), [overrideLabel, name]);

	const isFieldEditable = useMemo(
		() => overrideIsEditable(isEditable),
		[overrideIsEditable, isEditable],
	);

	const filterOptionsById = useMemo(
		() => overrideFieldOptionsFilter(null),
		[overrideFieldOptionsFilter],
	);
	// #endregion Common state

	// #region Actions
	const handleSubmit = useCallback(
		(values: NullableMultiGroupPickerOption, { onSuccess, onFail }: OnSubmitCallbacks) => {
			onSubmit?.(values);

			const onCompleted = fg('thor_inline-edit-actions-error-extraction')
				? createMutationErrorHandlers({ onSuccess, onFail })
				: // eslint-disable-next-line @typescript-eslint/no-explicit-any
					(response: any) => {
						if (response.jira?.updateMultipleGroupPickerField?.success) {
							onSuccess();
						} else {
							onFail();
						}
					};

			commit({
				variables: {
					input: {
						id: uniqueFieldId,
						operations: [
							{
								operation: 'SET',
								ids: values?.map((selection) => selection.id) ?? [],
							},
						],
					},
				},
				onCompleted,
				onError(error: Error) {
					onFail(error);
				},
				optimisticResponse: {
					jira: {
						updateMultipleGroupPickerField: {
							success: true,
							errors: null,
							field: {
								id: uniqueFieldId,
								name: fieldName,
								fieldId,
								type: fieldType,
								selectedGroupsConnection: {
									edges: values?.map((selection) => ({
										node: {
											id: selection.id,
											groupId: selection.groupId ?? '',
											name: selection.name,
										},
									})),
								},
							},
						},
					},
				},
			});
		},
		[commit, fieldId, fieldName, fieldType, onSubmit, uniqueFieldId],
	);

	const {
		invalidMessage,
		isEditing,
		hasServerValidationError,
		handleCancel,
		handleEdit,
		handleConfirm,
		handleChange,
		handleCopy,
		handleCut,
		handlePaste,
	} = useFieldInlineEditActions({
		attributes,
		fieldId,
		fieldName,
		fieldType,
		initialValue,
		isValueEqual: isEqualOption,
		onSubmit: handleSubmit,
		onSubmitFailed,
		onSubmitSucceeded,
		onUpdateValue: setUpdatedValue,
		startWithEditViewOpen,
		updatedValue,
		clipboardComponentType: 'multiGroupPicker',
	});
	// #endregion Actions

	// #region Read view
	const renderReadView = useCallback(
		() => (
			<MaybeUFOSegment>
				<MultiGroupPickerReadView
					fragmentRef={data}
					isTruncated={isTruncated}
					isTruncatedWithHoverPopover={isTruncatedWithHoverPopover}
				/>
			</MaybeUFOSegment>
		),
		[data, isTruncated, isTruncatedWithHoverPopover],
	);
	// #endregion

	// #region Edit view
	const getEditViewProps = (fieldProps: ValidationFieldProps): MultiGroupPickerEditViewProps => ({
		...fieldProps,
		autoFocus: true,
		fieldId: uniqueFieldId,
		value: updatedValue,
		menuPosition,
		onChange: handleChange,
		filterOptionsById,
		isMulti: true,
	});
	// #endregion Edit view

	return (
		<FieldInlineEditLiteWithEntryPoint
			editViewPopup={editViewPopup}
			editViewPopupAlignBlock={editViewPopupAlignBlock}
			editViewPopupMinWidth="small"
			editViewEntryPoint={MultiGroupPickerEditViewEntryPoint}
			editViewPopupTarget={editViewPopupTarget}
			editViewPopupWidth={editViewPopupWidth}
			editViewEntryPointParams={{}}
			getEditViewProps={getEditViewProps}
			fieldName={fieldName}
			hasUnsubmittedChanges={hasServerValidationError}
			invalidMessage={invalidMessage}
			isEditing={isEditing}
			isEditable={isFieldEditable}
			onCancel={handleCancel}
			onConfirm={handleConfirm}
			onEdit={handleEdit}
			onCopy={handleCopy}
			onCut={handleCut}
			onPaste={handlePaste}
			readViewFitContainerHeight={readViewFitContainerHeight}
			readView={renderReadView}
			hideActionButtons
			hasReadViewTruncateDisabled={isTruncatedWithHoverPopover}
		/>
	);
};

/**
 * Inline edit will handle the switching behaviour between the 'readView' and 'editView' components.
 *
 * @param props [MultiSelectInlineEditViewProps](./types.tsx)
 */
export const MultiGroupPickerInlineEditView = ({
	fragmentRef,
	...props
}: MultiGroupPickerInlineEditViewProps): React.JSX.Element => {
	// #region Relay
	const data =
		useFragment<ui_issueFieldMultiGroupPickerInlineEditFull_MultiGroupPickerInlineEditView_fragmentRef$key>(
			// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
			graphql`
				fragment ui_issueFieldMultiGroupPickerInlineEditFull_MultiGroupPickerInlineEditView_fragmentRef on JiraMultipleGroupPickerField {
					...ui_issueFieldMultiGroupPickerInlineEditFull_MultiGroupPickerInlineEditViewWithIsEditable_fragmentRef
					fieldConfig {
						isEditable
					}
				}
			`,
			fragmentRef,
		);
	// #endregion

	return (
		<MultiGroupPickerInlineEditViewWithIsEditable
			{...props}
			fragmentRef={data}
			isEditable={data.fieldConfig?.isEditable ?? false}
		/>
	);
};
